import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Button,
  Tag,
  Space,
  message,
  Spin,
  Typography,
  Row,
  Col,
  Table,
  Tabs,
  Alert,
} from 'antd';
import {
  ArrowLeftOutlined,
  ReloadOutlined,
  TrophyOutlined,
  TeamOutlined,
  CalendarOutlined,
  CrownOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { apiClient } from '../../../config/apiClient';
import MainLayout from '../../layout/layout';
import TorneoBracket from '../../../components/torneos/TorneoBracket';

const { Title, Text } = Typography;

const ESTADO_COLORS = {
  CAMPEON: 'gold',
  SUBCAMPEON: 'orange',
  ELIMINATORIA: 'blue',
  ZONA: 'cyan',
  CLASIFICADO: 'green',
  ELIMINADO: 'red',
  ELIMINADO_ZONA: 'red',
  ESPERANDO_GRUPOS: 'default',
  INSCRITO: 'default',
  FINALIZADO: 'purple',
};

const JugadorTorneoEnCurso = () => {
  const { torneoId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [vista, setVista] = useState(null);

  const loadVista = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get(`/torneos/mi/en-curso/${torneoId}`);
      if (res.ok) {
        setVista(await res.json());
      } else {
        const err = await res.json();
        message.error(err.message || 'Error al cargar el torneo');
        if (res.status === 403 || res.status === 404) {
          navigate('/jugador/mis-torneos');
        }
      }
    } catch (e) {
      console.error(e);
      message.error('Error al cargar el torneo');
    } finally {
      setLoading(false);
    }
  }, [torneoId, navigate]);

  useEffect(() => {
    loadVista();
  }, [loadVista]);

  if (loading && !vista) {
    return (
      <MainLayout>
        <div style={{ textAlign: 'center', padding: 48 }}><Spin size="large" /></div>
      </MainLayout>
    );
  }

  if (!vista) return null;

  const {
    torneo,
    inscripcion,
    mi_estado,
    mi_estado_label,
    es_campeon,
    grupo,
    llave,
    proximo_partido,
    modo_seguimiento,
  } = vista;

  const mostrarColumnaStb = torneo.tor_super_tie_break !== false && (torneo.tor_cantidad_sets ?? 3) === 3;

  const columnasTablaGrupo = [
    { title: '#', dataIndex: 'posicion', width: 40 },
    {
      title: 'Pareja',
      dataIndex: 'etiqueta',
      render: (v, r) => (
        <Text strong={r.soy_yo} style={{ color: r.soy_yo ? '#c4ff00' : '#fff' }}>{v}</Text>
      ),
    },
    { title: 'PJ', dataIndex: 'pj', width: 45 },
    { title: 'PG', dataIndex: 'pg', width: 45 },
    { title: 'Pts', dataIndex: 'puntos', width: 45 },
    { title: 'Games +/-', key: 'diff_games', width: 80, render: (_, r) => r.games_favor - r.games_contra },
    ...(mostrarColumnaStb
      ? [{
        title: 'Super TB +/-',
        key: 'diff_stb',
        width: 90,
        render: (_, r) => (r.stb_favor ?? 0) - (r.stb_contra ?? 0),
      }]
      : []),
  ];

  const renderProximoPartido = (partido) => {
    if (!partido) return null;
    if (partido.esperando_rival) {
      return (
        <Alert
          type="info"
          message="Esperando rival"
          description="Tu próximo cruce se definirá cuando termine el partido anterior."
          showIcon
        />
      );
    }
    return (
      <Card size="small" style={{ background: '#2a2a1a', border: '1px solid #c4ff0044' }}>
        <Text type="secondary">Próximo partido</Text>
        <div style={{ marginTop: 8 }}>
          <Text strong style={{ color: '#fff', fontSize: 16 }}>
            vs {partido.rival}
          </Text>
        </div>
        {partido.ronda_label && (
          <Tag color="blue" style={{ marginTop: 8 }}>{partido.ronda_label}</Tag>
        )}
        {partido.ronda && !partido.ronda_label && (
          <Tag color="blue" style={{ marginTop: 8 }}>Fase de zona</Tag>
        )}
      </Card>
    );
  };

  const tabItems = [];

  if (modo_seguimiento) {
    tabItems.push({
      key: 'seguimiento',
      label: 'Seguimiento',
      children: (
        <Alert
          type="info"
          showIcon
          message="Modo seguimiento"
          description="No participás en este torneo, pero podés ver cómo avanza la eliminatoria."
          style={{ marginBottom: 16 }}
        />
      ),
    });
  } else {
    tabItems.push({
      key: 'resumen',
      label: 'Resumen',
      children: (
        <div>
          {es_campeon && (
            <Alert
              type="success"
              icon={<CrownOutlined />}
              message="¡Felicitaciones, campeones!"
              description={`Ganaron el torneo ${torneo.tor_nombre}`}
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          <Card size="small" style={{ background: '#2d2d2d', border: '1px solid #434343', marginBottom: 16 }}>
            <Row gutter={[16, 12]}>
              <Col xs={24} sm={12}>
                <Text type="secondary">Mi pareja</Text>
                <div><Text strong style={{ color: '#fff' }}>{inscripcion.etiqueta_pareja}</Text></div>
              </Col>
              <Col xs={24} sm={12}>
                <Text type="secondary">Estado</Text>
                <div>
                  <Tag color={ESTADO_COLORS[mi_estado] || 'default'}>{mi_estado_label}</Tag>
                </div>
              </Col>
              {grupo && (
                <Col xs={24} sm={12}>
                  <Text type="secondary">Mi grupo</Text>
                  <div><Text strong style={{ color: '#fff' }}>Grupo {grupo.tgr_nombre}</Text></div>
                </Col>
              )}
              {grupo?.mi_posicion && (
                <Col xs={24} sm={12}>
                  <Text type="secondary">Posición en zona</Text>
                  <div><Text strong style={{ color: '#fff' }}>{grupo.mi_posicion}°</Text></div>
                </Col>
              )}
            </Row>
          </Card>

          {proximo_partido && (
            <div style={{ marginBottom: 16 }}>{renderProximoPartido(proximo_partido)}</div>
          )}

          {mi_estado === 'ESPERANDO_GRUPOS' && (
            <Alert type="info" message="El club está armando los grupos. Pronto vas a ver tu zona acá." />
          )}
        </div>
      ),
    });
  }

  if (grupo && !modo_seguimiento) {
    tabItems.push({
      key: 'zona',
      label: `Grupo ${grupo.tgr_nombre}`,
      children: (
        <div>
          <Title level={5} style={{ color: '#fff' }}>Tabla de posiciones</Title>
          <Table
            size="small"
            pagination={false}
            rowKey="tni_id"
            dataSource={grupo.tabla}
            rowClassName={(r) => (r.soy_yo ? 'torneo-row-yo' : '')}
            columns={columnasTablaGrupo}
            style={{ marginBottom: 24 }}
          />

          <Title level={5} style={{ color: '#fff' }}>Mis partidos</Title>
          <Table
            size="small"
            pagination={false}
            rowKey="id"
            dataSource={grupo.mis_partidos}
            columns={[
              { title: 'Rival', dataIndex: 'rival' },
              {
                title: 'Resultado',
                key: 'res',
                width: 180,
                render: (_, r) => (
                  r.resultado
                    ? <Tag color={r.ganamos ? 'green' : 'red'} style={{ whiteSpace: 'normal' }}>{r.resultado}</Tag>
                    : <Tag>Pendiente</Tag>
                ),
              },
            ]}
          />
        </div>
      ),
    });
  }

  if (llave?.llave_generada) {
    tabItems.push({
      key: 'llave',
      label: 'Llave',
      children: (
        <div>
          {llave.mis_partidos?.length > 0 && (
            <>
              <Title level={5} style={{ color: '#fff' }}>Mis partidos eliminatorios</Title>
              <Table
                size="small"
                pagination={false}
                rowKey="id"
                dataSource={llave.mis_partidos}
                style={{ marginBottom: 24 }}
                columns={[
                  { title: 'Ronda', dataIndex: 'ronda_label' },
                  { title: 'Rival', dataIndex: 'rival' },
                  {
                    title: 'Resultado',
                    key: 'res',
                    render: (_, r) => (
                      r.esperando_rival
                        ? <Tag>Esperando rival</Tag>
                        : r.resultado
                          ? <Tag color={r.ganamos ? 'green' : 'red'} style={{ whiteSpace: 'normal' }}>{r.resultado}</Tag>
                          : <Tag color="blue">Pendiente</Tag>
                    ),
                  },
                ]}
              />
            </>
          )}

          <Title level={5} style={{ color: '#fff' }}>Llave completa</Title>
          <TorneoBracket
            porRonda={llave.por_ronda}
            rondaLabels={Object.fromEntries(
              Object.entries(llave.por_ronda || {}).map(([k, v]) => [k, v.label])
            )}
            highlightMine={!modo_seguimiento}
          />
        </div>
      ),
    });
  }

  return (
    <MainLayout>
      <style>{`
        .torneo-row-yo td { background: rgba(196, 255, 0, 0.08) !important; }
      `}</style>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '16px 12px 32px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
          <Space>
            <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/jugador/mis-torneos')} />
            <div>
              <Title level={3} style={{ color: '#fff', margin: 0 }}>
                <TrophyOutlined /> {torneo.tor_nombre}
              </Title>
              <Space size="small" style={{ marginTop: 4 }}>
                {torneo.tor_fecha && (
                  <Text type="secondary">
                    <CalendarOutlined /> {dayjs(torneo.tor_fecha).format('DD/MM/YYYY')}
                  </Text>
                )}
                <Tag color="blue">{torneo.fase_label}</Tag>
                {modo_seguimiento ? (
                  <Tag color="purple">Seguimiento</Tag>
                ) : (
                  <Tag color={ESTADO_COLORS[mi_estado] || 'default'}>{mi_estado_label}</Tag>
                )}
              </Space>
            </div>
          </Space>
          <Button icon={<ReloadOutlined />} onClick={loadVista} loading={loading}>Actualizar</Button>
        </div>

        <Tabs items={tabItems} />
      </div>
    </MainLayout>
  );
};

export default JugadorTorneoEnCurso;
